import { useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '@clerk/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ApiError, createApiClient } from './client.js'
import { ApiClientContext } from './contexts.js'

// Holds the HTTP client (Clerk token getter behind a ref)
// and the TanStack QueryClient. Mount inside ClerkProvider.
//
// `client` prop is a test-only override.
export function ApiProvider({ children, client: clientOverride }) {
  const { getToken } = useAuth()
  const getTokenRef = useRef(getToken)

  useEffect(() => {
    getTokenRef.current = getToken
  }, [getToken])

  const client = useMemo(
    () => clientOverride || createApiClient((opts) => getTokenRef.current?.(opts)),
    [clientOverride],
  )

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 15_000,
            refetchOnWindowFocus: true,
            retry: (failureCount, error) => {
              // Never retry client errors (4xx) — they won't get better.
              if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false
              return failureCount < 2
            },
          },
        },
      }),
  )

  return (
    <QueryClientProvider client={queryClient}>
      <ApiClientContext.Provider value={client}>{children}</ApiClientContext.Provider>
    </QueryClientProvider>
  )
}
